import { FC, useMemo } from "react";
import { useTable } from "spacetimedb/react";
import { tables } from "@module-bindings";
import type { Candlestick, Market } from "@module-bindings/types";
import { assetIdKey, fixQuery } from "@/lib/marketUtils";
import { cn } from "@/lib/utils";

const FRAC_BITS = 60n;

function priceToNumber(raw: bigint): number {
  const integer = Number(raw >> FRAC_BITS);
  const fractional = Number(raw & ((1n << FRAC_BITS) - 1n)) / Number(1n << FRAC_BITS);
  return integer + fractional;
}

interface MarketStatsProps {
  selectedMarket: Market | null;
  className?: string,
  itemClassName?: string,
  columnsClassName?: string,
}

export const useMarketStats = (selectedMarket: Market | null) => {
  if (!selectedMarket) return [];

  const pairKey = `${assetIdKey(selectedMarket.tradingPair.base)}/${assetIdKey(selectedMarket.tradingPair.quote)}`;

  const [tpsRows] = useTable(tables.tps_stats);
  const query = fixQuery(tables.candlesticks.where(c => c.pairKey.eq(pairKey).and(c.granularity.ne("1m"))), [['pairKey', 'pair_key']]);
  const [candles] = useTable(query);

  const tps = tpsRows.length > 0 ? (tpsRows[0] as any).lastSecondCount ?? 0 : 0;
  const totalEvents = tpsRows.length > 0 ? tpsRows[0].totalEvents ?? 0n : 0n;

  // Get hourly candle for volume
  // and daily candle for 24h stats
  const { hourlyCandle, dailyCandle } = useMemo(() => {
    if (!pairKey) return { hourlyCandle: null, dailyCandle: null };
    let hourlyCandle: Candlestick | null = null;
    let dailyCandle: Candlestick | null = null;
    for (const candle of candles) {
      if (candle.granularity === "1h" && candle.timestamp > (hourlyCandle?.timestamp ?? 0n)) {
        hourlyCandle = candle;
      }
      if (candle.granularity === "1d" && candle.timestamp > (dailyCandle?.timestamp ?? 0n)) {
        dailyCandle = candle;
      }
    }
    return { hourlyCandle, dailyCandle };
  }, [candles, pairKey]);

  const hourlyVolume = hourlyCandle ? Number(hourlyCandle.volume) : 0;
  const hourlyTrades = hourlyCandle ? hourlyCandle.tradeCount : 0;

  const dayHigh = dailyCandle ? priceToNumber(dailyCandle.high) : null;
  const dayLow = dailyCandle ? priceToNumber(dailyCandle.low) : null;

  return [
    {
      label: "Volume (1h)",
      value: hourlyVolume > 0 ? formatVolume(hourlyVolume) : "—",
      placeholder: hourlyVolume === 0,
    },
    {
      label: "24h High",
      value: dayHigh != null ? dayHigh.toFixed(6) : "—",
      placeholder: dayHigh == null,
    },
    {
      label: "24h Low",
      value: dayLow != null ? dayLow.toFixed(6) : "—",
      placeholder: dayLow == null,
    },
    {
      label: "Trades (1h)",
      value: hourlyTrades > 0 ? hourlyTrades.toLocaleString() : "—",
      placeholder: hourlyTrades === 0,
    },
    {
      label: "TPS",
      value: tps.toString(),
      sub: `${totalEvents.toLocaleString()} total`,
    },
  ];
}

export const MarketStats: FC<MarketStatsProps> = ({
  selectedMarket,
  className,
  itemClassName,
  columnsClassName,
}) => {
  const stats = useMarketStats(selectedMarket);

  return (
    <div className={cn("grid h-fit grid-cols-2 gap-1 lg:grid-cols-5", columnsClassName, className)}>
      {stats.map((stat) => (
        <div
          key={stat.label}
          className={cn("sr-panel flex min-h-[92px] flex-1 items-start px-5 py-4", itemClassName)}
        >
          <div className="flex h-fit flex-col">
            <span className="mb-1.5 block text-[11px] font-medium text-sr-neutral-300">
              {stat.label}
            </span>
            <span className={`block text-base font-medium font-display ${stat.value === "—" ? "text-sr-neutral-700" : "text-white"}`}>
              {stat.value}
            </span>
            {"sub" in stat && stat.sub && (
              <span className="mt-0.5 block text-[11px] text-sr-neutral-300">
                {stat.sub}
              </span>
            )}
          </div>
        </div>
      ))}
    </div>
  );
};

function formatVolume(v: number): string {
  if (v >= 1_000_000_000) return `${(v / 1_000_000_000).toFixed(2)}B`;
  if (v >= 1_000_000) return `${(v / 1_000_000).toFixed(2)}M`;
  if (v >= 1_000) return `${(v / 1_000).toFixed(1)}K`;
  return v.toLocaleString();
}
